import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { LoadingState } from "../../../../../components/primitives/feedback/LoadingState";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../../components/primitives/data-display/DataSequence";
import { Note } from "../../../../../components/primitives/data-display/Note";
import { Statistic } from "../../../../../components/primitives/data-display/Statistic";
import { getBatchJobItemRowId } from "../../panel/batch_job_item_rows";
import {
  COVERAGE_GAP_RECHECK_TAB_ORDER,
  COVERAGE_GAP_RECHECK_TABS,
} from "./coverage_gap_recheck_tabs";
import type {
  CoverageGapRecheckPanelSummary,
  CoverageGapRecheckRow,
  UseCoverageGapRecheckPanelResult,
} from "./useCoverageGapRecheckPanel";

function SummaryHeader({ summary }: { summary: CoverageGapRecheckPanelSummary | null }) {
  if (!summary) {
    return <Note>The summary appears once the job has finished.</Note>;
  }
  return (
    <Stack direction="row" gap={4} flexWrap="wrap">
      <Statistic label="now answered">{summary.answeredCount}</Statistic>
      <Statistic label="still unanswered">{summary.unansweredCount}</Statistic>
    </Stack>
  );
}

/** The verdict the claim got on this check, or why the check failed. */
function VerdictCell({ row }: { row: CoverageGapRecheckRow }) {
  if (!row.result) {
    return row.item.error ? (
      <Typography variant="caption" color="error">
        {row.item.error}
      </Typography>
    ) : (
      <>—</>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary">
      {row.result.verdict}
    </Typography>
  );
}

export interface CoverageGapRecheckPanelBodyProps {
  panel: UseCoverageGapRecheckPanelResult;
}

/** Presentational coverage_gap_recheck results panel: a summary of the check, then one tab per
 *  outcome, each a paginated list of claims. Read-only. */
export function CoverageGapRecheckPanelBody({ panel }: CoverageGapRecheckPanelBodyProps) {
  const columns: DataSequenceColumn<CoverageGapRecheckRow>[] = [
    {
      key: "claim",
      header: "Claim",
      width: 420,
      cardRole: "primary",
      cell: (row) => row.item.subjectTitle,
    },
    { key: "verdict", header: "Verdict now", cell: (row) => <VerdictCell row={row} /> },
  ];

  return (
    <Stack gap={2}>
      <SummaryHeader summary={panel.summary} />

      <Tabs
        value={panel.tabs.activeTab}
        onChange={(_e, v: string) => panel.tabs.setActiveTab(v as typeof panel.tabs.activeTab)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
      >
        {COVERAGE_GAP_RECHECK_TAB_ORDER.map((key) => (
          <Tab key={key} label={COVERAGE_GAP_RECHECK_TABS[key].label} value={key} />
        ))}
      </Tabs>

      {panel.list.error && <Alert severity="error">{panel.list.error}</Alert>}

      {panel.list.loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={panel.list.rows}
          getRowId={getBatchJobItemRowId}
          emptyMessage="No claims in this list."
          pagination={panel.list.pagination}
        />
      )}
    </Stack>
  );
}
